import React, { useRef } from 'react';
import { useIntl } from 'react-intl';
import { Button, CircularProgress } from '@mui/material';
import { useVirtualizer } from '@tanstack/react-virtual';

import styles from './HaOverviewList.module.scss';

const ESTIMATED_CARD_HEIGHT = 64;

type HaOverviewListProps = {
  rowIds: string[];
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  renderRow: (seriesId: string) => React.ReactNode;
};

export function HaOverviewList({ rowIds, loading, error, onRetry, renderRow }: HaOverviewListProps) {
  const intl = useIntl();
  const containerRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: rowIds.length,
    getScrollElement: () => containerRef.current,
    estimateSize: () => ESTIMATED_CARD_HEIGHT,
    getItemKey: (index) => rowIds[index],
    overscan: 0,
  });

  if (error) {
    return (
      <div className={styles.list_container}>
        <div className={styles.error}>
          <div>{intl.formatMessage({ id: 'common.defaultError' })}</div>
          <Button variant="outlined" onClick={onRetry}>
            {intl.formatMessage({ id: 'common.retry', defaultMessage: 'Retry' })}
          </Button>
        </div>
      </div>
    );
  }

  if (loading && rowIds.length === 0) {
    return (
      <div className={styles.list_container}>
        <div className={styles.loading}>
          <CircularProgress size={32} />
        </div>
      </div>
    );
  }

  if (rowIds.length === 0) {
    return (
      <div className={styles.list_container}>
        <div className={styles.empty}>
          {intl.formatMessage({ id: 'seriesbooking.haOverview.emptyState' })}
        </div>
      </div>
    );
  }

  const virtualItems = virtualizer.getVirtualItems();

  return (
    <div className={styles.list_container} ref={containerRef}>
      <div className={styles.list} style={{ height: `${virtualizer.getTotalSize()}px` }}>
        {virtualItems.map((virtualItem) => (
          <div
            key={String(virtualItem.key)}
            data-index={virtualItem.index}
            ref={virtualizer.measureElement}
            className={styles.row}
            style={{ transform: `translateY(${virtualItem.start}px) translateZ(0)` }}
          >
            {renderRow(rowIds[virtualItem.index])}
          </div>
        ))}
      </div>
    </div>
  );
}
